import { Box, Container, styled } from '@mui/material';
import { FilterParams } from '@synergy/models';
import { FormattedMessage } from 'react-intl';
import { PoolRow } from '@synergy/components-next';
import { ReactElement } from 'react';
import { SortingArrowLeftIcon } from '@synergy/theme-next';
import { useSearchParams } from 'react-router-dom';

import { CategoryBarButton } from './components/category-bar-button';
import { articleSearchResultsCategorySelector } from '../../../store/articles-selectors';
import { useAppSelector } from '../../../../store/store';

const StyledBox = styled(Box, { shouldForwardProp: prop => prop !== 'columns' })(({ theme }) => ({
    width: '100%',
    display: 'grid',
    gridTemplateColumns: `repeat(auto-fill, minmax(287px, 1fr))`,
    gap: theme.spacing(2),
    padding: theme.spacing(3, 0),
    minHeight: theme.spacing(13),

    [theme.breakpoints.down('sm')]: {
        minHeight: theme.spacing(11),
        padding: theme.spacing(2, 0),
        display: 'flex',
        overflowX: 'scroll',
        scrollbarWidth: 'none' /* Firefox */,
        MsOverflowStyle: 'none' /* IE and Edge */,
        '&::-webkit-scrollbar': {
            display: 'none' /* Chrome, Safari and Opera */,
        },
    },
}));

interface CategoryBarProps {
    categorySelector?: (categoryId: number) => {
        parentCategory: { id: number; name: string } | undefined;
        currentCategory: { id: number; name: string } | undefined;
        subCategories: Array<{ id: number; name: string; count: number }>;
    };
    showBackButtonWithSearch?: boolean;
}

export const CategoryBar = ({ categorySelector, showBackButtonWithSearch = true }: CategoryBarProps): ReactElement => {
    const [searchParams, setSearchParams] = useSearchParams();
    const selectedCategoryId = Number(searchParams.get(FilterParams.CategoryId));
    const defaultCategorySelector = useAppSelector(articleSearchResultsCategorySelector);
    const { parentCategory, currentCategory, subCategories } = (categorySelector ?? defaultCategorySelector)(
        selectedCategoryId
    );

    const handleCategorySelect = (categoryId?: number): void => {
        searchParams.delete(FilterParams.CategoryId);
        searchParams.delete(FilterParams.Page);
        if (categoryId !== undefined) {
            searchParams.set(FilterParams.CategoryId, categoryId.toString());
        }
        setSearchParams(searchParams);
    };

    const shouldShowBackButton = showBackButtonWithSearch
        ? currentCategory && (parentCategory || searchParams.get(FilterParams.Search))
        : currentCategory;

    return (
        <Box sx={{ backgroundColor: theme => theme.palette.grey[100] }}>
            <Container maxWidth="xl" sx={{ pr: { xs: 0, sm: 3 } }}>
                <StyledBox>
                    {shouldShowBackButton && (
                        <CategoryBarButton onClick={() => handleCategorySelect(parentCategory?.id)}>
                            <PoolRow>
                                <SortingArrowLeftIcon fontSize="large" />
                                <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'pre' }}>
                                    <strong>
                                        {parentCategory ? (
                                            parentCategory.name
                                        ) : (
                                            <FormattedMessage id="app.filter.allCategories" />
                                        )}
                                    </strong>
                                </Box>
                            </PoolRow>
                        </CategoryBarButton>
                    )}
                    {subCategories.map(category => (
                        <CategoryBarButton
                            key={category.id}
                            count={category.count}
                            onClick={() => handleCategorySelect(category.id)}
                        >
                            {category.name}
                        </CategoryBarButton>
                    ))}
                </StyledBox>
            </Container>
        </Box>
    );
};
